<template lang="pug">
div
    v-snackbar(
        v-model='snackbar.show'
        :color='snackbar.color'
        :timeout='snackbar.timeout'
        top
    )
        | {{ snackbar.text }}
        v-btn(dark text @click='snackbar.show = false')
            | Close
    .fullscreen.brown.lighten-5.pt-3
        BigBoardMenu(
            :cookie-settings-object="cookieSettingsObject"
            :current-tab="currentTab"
            :get-rules="getRules"
            :set-current-tab="setCurrentTab"
            :shared-link-mode="sharedLinkMode"
            :show-snackbar="showSnackbar"
            :rules="rules"
            :scope-parameters="scopeParameters"
        )
        TableIndex(
            ref="tableIndex"
            :agents="sortedAgents"
            :agents-loading="agentsLoading"
            :calls-waiting="callsWaiting"
            :cookie-settings-object="cookieSettingsObject"
            :current-day-dashboard-summary="currentDayDashboardSummary"
            :current-base-dashboard-summary="currentBaseDashboardSummary"
            :current-tab="currentTab"
            :get-users-in-organization="getUsersInOrganization"
            :rules="rules"
            :scope-parameters="scopeParameters"
        )
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from "@/store";

// Components
import BigBoardMenu from '@/components/dashboards/big_board/menu.vue';
import TableIndex from '@/components/dashboards/big_board/index.vue';

//Data
import { GetRules, GetCallsWaiting, GetLiveDashboardAgents, MatchOrCreateBaseDashboardSummary, GetCurrentBaseDashboardSummaryForSharedLink, GetAllUsersInOrganization, GetBaseDashboardSummaries } from "@/data";
export default Vue.extend({
    beforeCreate() {
        return new DataBank({
            lazyResources: ['teams'],
        });
    },
    data() {
        return {
            agents: [],
            agentsClockInterval: null,
            agentsLoading: false,
            bus: VolieState.websocketClient().eventBus(),
            callsWaiting: [],
            clockInterval: null,
            cookieSettingsObject: {
                pauseAgentScrollOnHover: true,
                pauseTableScrollOnHover: true,
                showUserCardSection: true,
                hideUnscopedAgentsInCardSection: true,
                
                agentScrollSpeedMultiplier: '1',
                tableScrollSpeedMultiplier: '1',
            },
            currentTab: "account-view",
            organizationUsers: [],
            isLoadingRules: false,
            rules: [],
            scopeParameters: {
                displayView: '',
                scopeType: '',
                scopeId: 0,
            },
            snackbar: {
                color: "",
                show: false,
                text: "",
                timeout: 2500,
            },
            summaryPollInterval: null,
            usersInOrganization: new Map<number, any>(),
        }
    },
    methods: {
        getCallsWaiting() {
            GetCallsWaiting().then((response) => {
                if (response?.Data?.calls_waiting?.length > 0) {
                    this.callsWaiting = response?.Data?.calls_waiting;
                }
            });
        },
        getDashboardAgents() {
            this.agentsLoading = true;
            const accountId = this.accountId || (this.currentUser?.activated_account_id || 0);
            GetLiveDashboardAgents({ account_id: accountId }).then((response) => {
                if (response?.Data?.agents?.length > 0) {
                    this.agents = response?.Data?.agents;
                }
                this.agentsLoading = false;
            });
        },
        getRules() {
            const options = {
                purpose: "big_board_account",
            }

            this.isLoadingRules = true;
            GetRules(options).then((response) => {
                this.rules = response?.Data?.rules;
                this.isLoadingRules = false;
            });
        },
        async getUsersInOrganization(organizationId: number) {
            if (this.usersInOrganization.has(organizationId)) {
                return this.usersInOrganization.get(organizationId);
            }

            this.usersInOrganization.set(organizationId, []);

            await GetAllUsersInOrganization({ organization_ids: [organizationId] }).then((response) => {
                this.usersInOrganization.set(organizationId, response?.Data?.users || []);
            });

            return this.usersInOrganization.get(organizationId);
        },
        matchOrCreateBaseDashboardSummary() {

            // Init
            const options = {
                owner_id: (this.currentUser?.user_id || 0),
                owner_type: "user",
                time_zone_location: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
            }

            MatchOrCreateBaseDashboardSummary(options).then(() => {
            });
        },
        pollBaseDashboardSummaries() {
            GetBaseDashboardSummaries().then((response) => {
                // null Data means an error response — keep the current store state to
                // avoid flashing "No data found" on a transient server-side failure
                const summaries = response?.Data?.base_dashboard_summaries;
                if (summaries == null) return;
                VolieState.applyPolledBaseDashboardSummaries(summaries);
            });
        },
        startSummaryPoll() {
            // Shared-link init only loads a user-owned summary; poll backfills org/team Account View rows.
            this.pollBaseDashboardSummaries();
            this.summaryPollInterval = setInterval(this.pollBaseDashboardSummaries, 300000);
        },
        stopSummaryPoll() {
            if (this.summaryPollInterval) {
                clearInterval(this.summaryPollInterval);
                this.summaryPollInterval = null;
            }
        },
        init() {
            if (!this.sharedLinkMode) {
                this.matchOrCreateBaseDashboardSummary(null);
            } else {
                // Load initial base summary for shared-link viewers (read-only)
                const options = {
                    owner_id: (this.currentUser?.user_id || 0),
                    owner_type: "user",
                };
                GetCurrentBaseDashboardSummaryForSharedLink(options).then((response) => {
                    const payload = response?.Data?.data;
                    if (payload?.base_dashboard_summary?.id > 0) {
                        // Commit a synthetic push to reuse existing merge logic in the store
                        this.bus.send({ type: 'broadcast', channel: { Type: 'baseDashboardSummaryUpdates', ID: null }}, { Type: 'broadcast', Data: payload });
                    }
                });
            }
            this.startClock();
            this.getDashboardAgents();
            this.getRules();
            this.startSummaryPoll();
        },
        onCallCommunicationQueueUpdate(response) {
            // Verify legit call communication queue
            const ccq = response?.Data;
            if (!(ccq?.call_communication_queue_id > 0) || !(ccq?.account_id > 0) || !(ccq?.status?.length > 0)) {
                return;
            }

            // Verify account matches is current user's account if not a shared link
            if (this.currentUser?.activated_account_id !== ccq.account_id && !this.sharedLinkMode) {
                return;
            }

            // Determine if record is in array
            const idx = (this.callsWaiting || []).findIndex(i => i.call_communication_queue_id === ccq.call_communication_queue_id);
            if (idx >= 0) {

                // If status is not waiting, remove from array
                if (ccq.status !== "waiting" && ccq.status !== "ivr_callback") {
                    this.callsWaiting.splice(idx, 1);
                }
            } else if (ccq.status === "waiting" || ccq.status === "ivr_callback") {

                // If the status is waiting and it isn't currently in our array, add it
                this.callsWaiting.push(ccq);

                // Re-sort just in case
                this.callsWaiting = this.sortCallsWaitingByID();
            }
        },
        onLatestAgent(response) {

            // Note - the incoming agent record is made from the PushLatestAgentData struct, the page agents are LiveDashboardAgent

            // Verify legit agent
            const agent = response?.Data;
            if (!(agent?.user_id > 0) || !(agent?.account_id > 0) || !(agent?.account_user_id > 0)) {
                return;
            }

            // Verify account matches is current user's account if not a shared link
            if (this.currentUser?.activated_account_id !== agent.account_id && !this.sharedLinkMode) {
                return;
            }

            // Attempt to find existing agent. If we find it, just need to update fields
            const idx = (this.agents || []).findIndex(i => i.account_user_id === agent.account_user_id);
            if (idx >= 0) {

                // Update fields on existing ref
                const existingAgent = this.agents[idx];

                // If the agent ID on the existing record is newer than the push, ignore
                if (!(existingAgent?.account_user_id > 0) || existingAgent?.last_agent_id > agent.agent_id) {
                    return;
                }

                // Set fields
                existingAgent.last_agent_id = agent.agent_id;
                existingAgent.last_agent_status_id = agent.agent_status_id;
                existingAgent.agent_created_at = agent.created_at;
                existingAgent.agent_status_name = agent.agent_status_name;
                existingAgent.agent_status_type = agent.status_type;
                existingAgent.appointments_created_today = agent.appointments_created_today;
                existingAgent.call_id = agent.call_id;
                existingAgent.campaign_id = agent.campaign_id;
                existingAgent.campaign_name = agent.campaign_name;
                existingAgent.communication_type_name = agent.communication_type_name;
                existingAgent.communication_provider_id = agent.communication_provider_id;
                existingAgent.inbound_calls_today = agent.inbound_calls_today;
                existingAgent.is_online = agent.is_online;
                existingAgent.organization_id = agent.organization_id;
                existingAgent.organization_name = agent.organization_name;
                existingAgent.outbound_calls_today = agent.outbound_calls_today;
                existingAgent.time_in_seconds = null;
                existingAgent.total_calls = agent.total_calls;

            } else {

                // Add record to array and resort
                const BigBoardAgent = this.pushLatestAgentDataToDashboardAgent(agent);
                if (!(BigBoardAgent?.account_user_id > 0)) {
                    return;
                }

                this.agents.push(BigBoardAgent);
            }
        },
        pushLatestAgentDataToDashboardAgent(agent) {
            if (!agent) {
                return;
            }

            return {
                account_user_id: agent.account_user_id,
                account_id: agent.account_id,
                campaign_id: agent.campaign_id,
                default_role_id: agent.default_role_id,
                last_agent_id: agent.agent_id,
                last_agent_status_id: agent.agent_status_id,
                organization_id: agent.organization_id,
                user_id: agent.user_id,
                agent_created_at: agent.created_at,
                agent_status_name: agent.agent_status_name,
                agent_status_type: agent.status_type,
                assigned_campaign_customer_actives: [],
                campaign_name: agent.campaign_name,
                communication_type_name: agent.communication_type_name,
                inbound_calls_today: agent.inbound_calls_today,
                is_online: agent.is_online,
                organization_name: agent.organization_name,
                outbound_calls_today: agent.outbound_calls_today,
                role_name: agent.role_name,
                time_in_seconds: null,
                total_assigned_return_message_campaign_customers: agent.total_assigned_return_message_campaign_customers,
                total_calls: agent.total_calls,
                unresponded_to_incoming_message_at: null,
                unresponded_to_incoming_message_id: agent.unresponded_to_incoming_message_id,
                unresponded_to_time_in_seconds: null,
                user_email: agent.email,
                user_full_name: agent.full_name,
            }
        },
        setClockAgentValues() {

            // Verify render agents
            if (!(this.agents?.length > 0)) {
                return;
            }

            // Init
            const currentTime = new Date();

            // Loop through agents and set time in seconds
            for (let i = 0; i < this.agents.length; i++) {

                // Init agent
                const agent = this.agents[i];
                if (!agent || !agent.agent_created_at) {
                    continue;
                }

                // Init agent time
                const agentTime = new Date(agent.agent_created_at);

                // Calculate clock
                agent.time_in_seconds = (Math.floor(currentTime.getTime() - agentTime.getTime()) / 1000);

                // Handle unresponded to time - this may very well be null if the agent isn't on sms screen or if they are waiting for responses
                if (!!agent.unresponded_to_incoming_message_at) {

                    // Init time
                    const unrespondedToTime = new Date(agent.unresponded_to_incoming_message_at);
                    if (!!unrespondedToTime) {

                        // Calculate clock
                        agent.unresponded_to_time_in_seconds = (Math.floor(currentTime.getTime() - unrespondedToTime.getTime()) / 1000);
                    }

                } else {
                    agent.unresponded_to_time_in_seconds = null;
                }
            }
        },
        setClockValues() {

            // Check if we have calls waiting
            if (this.callsWaiting?.length > 0) {

                // Init
                const currentTime = new Date();

                // Loop through calls and set time in seconds
                for (let i = 0; i < this.callsWaiting.length; i++) {

                    // Init agent
                    const ccq = this.callsWaiting[i];
                    if (!ccq || !ccq.start_time) {
                        continue;
                    }

                    // Init agent time
                    const ccqTime = new Date(ccq.start_time);

                    // Calculate clock
                    ccq.hold_time_in_seconds = (Math.floor(currentTime.getTime() - ccqTime.getTime()) / 1000);
                }
            }
        },
        setCurrentTab(tab: string) {
            this.currentTab = tab;

            this.scopeParameters.displayView = '';
            this.scopeParameters.scopeType = '';
            this.scopeParameters.scopeId = '';
        },
        showSnackbar(color, text) {
            if (color?.length > 0 && text?.length > 0) {
                this.snackbar.color = color;
                this.snackbar.text = text;
                this.snackbar.show = true;
            }
        },
        sortCallsWaitingByID() {
            if (!(this.callsWaiting?.length > 0)) {
                return [];
            }

            return this.callsWaiting.slice(0).sort(function(x, y){
                return x?.call_communication_queue_id - y?.call_communication_queue_id;
            });
        },
        startClock() {
            this.clockInterval = setInterval(this.setClockValues, 1000);
            this.agentsClockInterval = setInterval(this.setClockAgentValues, 1000);
        },
        stopClock() {
            if (!this.clockInterval) {
                return;
            }

            clearInterval(this.clockInterval);
            clearInterval(this.agentsClockInterval);
        },
    },
    computed: {
        baseDashboardSummaries() {
            return VolieState.baseDashboardSummaries();
        },
        currentBaseDashboardSummary() {
            return VolieState.currentBaseDashboardSummary();
        },
        currentDayDashboardSummary() {
            
            // Verify (this could be null during initial load)
            if (!this.validBaseDashboardSummary || !this.validDashboardSummariesForCurrent) {
                return {};
            }
            
            // Loop over day summaries and grab day summary (will only be one)
            for (let i = 0; i < this.currentBaseDashboardSummary?.data?.dashboard_summary_periods?.length; i++) {
                
                const record = this.currentBaseDashboardSummary?.data?.dashboard_summary_periods[i];
                if (!record || !(record.id > 0)) {
                    continue;
                }

                if (record.period_type !== "day") {
                    continue;
                }
                
                return record;
            }
            
            return {};
        },
        currentUser() {
            return VolieState.currentUser();
        },
        sortedAgents() {
            if (!(this.agents?.length > 0)) {
                return [];
            }

            return this.agents
                .filter(agent => agent.is_online && agent.role_name !== "super_admin")
                .slice()
                .sort((a, b) => {
                    return (a.user_full_name || '').localeCompare(b.user_full_name || '');
                });
        },
        validBaseDashboardSummary() {
            return this.currentBaseDashboardSummary?.base_dashboard_summary?.id > 0;
        },
        validDashboardSummariesForCurrent() {
            return this.validBaseDashboardSummary && this.currentBaseDashboardSummary?.dashboard_summaries?.length > 0;
        },
    },
    components: {
        BigBoardMenu,
        TableIndex,
    },
    props: {
        accountId: Number,
        sharedLinkMode: Boolean,
    },
    watch: {
    },
    created() {
        this.init();
        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "agent" }}, this.onLatestAgent);
        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "callCommunicationQueueUpdate" }}, this.onCallCommunicationQueueUpdate);
    },
    destroyed() {
        this.stopClock();
        this.stopSummaryPoll();
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "agent" }}, this.onLatestAgent);
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "callCommunicationQueueUpdate" }}, this.onCallCommunicationQueueUpdate);
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>
#app {
    .fullscreen {
        position: fixed;
        z-index: 1;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background-color: #3b3f50 !important;
    }
}
</style>
